import { buildApiUrl, tenantApiPrefix } from '../config';
import { getTenantIdFromStorage } from '../utils/onboardingLocalStorage';

/**
 * The carrier numbers, shared by every agent that rings a carrier.
 *
 * Its own service rather than one agent's, because the table is one table: a
 * number set on Policy Servicing's configuration screen is the number Carrier
 * Correspondence dials, and two copies of this that drifted apart would mean a
 * call going to the wrong phone with nothing on screen to say why.
 *
 * The carrier names come from the workspace's own warehouse; the numbers are
 * ours, because that warehouse is read-only and could never hold them.
 */

export interface Carrier {
  name: string;
  /** null when nobody has given us a number: the agent then refuses to call. */
  phone: string | null;
  updated_at: string | null;
}

export interface Carriers {
  carriers: Carrier[];
  without_a_number: number;
  warehouse_error: string | null;
}

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('klaris_jwt');
  return { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) };
}

function prefix(): string {
  const tid = getTenantIdFromStorage();
  return `${tenantApiPrefix(tid ?? '')}/carriers`;
}

async function call<T>(init: RequestInit, fallback: string): Promise<T> {
  const response = await fetch(buildApiUrl(prefix()), { ...init, headers: getAuthHeaders() });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const detail = body && typeof body === 'object' ? (body as { detail?: unknown }).detail : null;
    throw new Error(typeof detail === 'string' ? detail : fallback);
  }
  return response.json();
}

export const fetchCarriers = () =>
  call<Carriers>({ method: 'GET' }, 'Could not load the carriers');

/** Save one or more numbers. Carriers left out are not touched. */
export const saveCarriers = (changes: { name: string; phone: string }[]) =>
  call<Carriers>({ method: 'PUT', body: JSON.stringify(changes) }, 'Could not save the numbers');
